import React, { useCallback, useMemo, useState } from "react";
import { Icon } from "./icons";
import { useDismiss } from "./ui";
import { useFilters } from "./filters";
import { NAMES, workspaceLabel } from "./data";
import { compact } from "./format";

/** A searchable workspace filter: every workspace active in the period, busiest first. */
export default function WorkspacePicker({ accounts }) {
  const { f, set } = useFilters();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);

  const list = useMemo(() => {
    const rows = [...(accounts ?? [])].sort((a, b) => b.runs - a.runs);
    const needle = q.trim().toLowerCase();
    return needle
      ? rows.filter((a) => [workspaceLabel(a.accountId), NAMES.accounts.get(a.accountId)?.slug, a.accountId].some((t) => String(t ?? "").toLowerCase().includes(needle)))
      : rows;
  }, [accounts, q]);

  const choose = (id) => {
    set({ ws: id || null });
    setOpen(false);
    setQ("");
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") setActive((i) => Math.min(list.length, i + 1));
    else if (e.key === "ArrowUp") setActive((i) => Math.max(0, i - 1));
    else if (e.key === "Enter") choose(active === 0 ? "" : list[active - 1]?.accountId);
    else return;
    e.preventDefault();
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" className="aa-btn" onClick={() => setOpen((o) => !o)} aria-expanded={open} style={{ maxWidth: 260 }}>
        <Icon name="workspaces" size={15} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{f.ws ? workspaceLabel(f.ws) : "All workspaces"}</span>
        <Icon name="chevronDown" size={14} />
      </button>
      {open && (
        <div className="aa-pop" style={{ right: 0, top: 42, width: 320, padding: 8 }}>
          <div style={{ position: "relative", marginBottom: 6 }}>
            <span style={{ position: "absolute", left: 10, top: 9, color: "var(--aa-faint)" }}>
              <Icon name="search" size={15} />
            </span>
            <input
              autoFocus
              className="aa-input"
              placeholder="Search workspaces"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKey}
              style={{ width: "100%", paddingLeft: 32, boxShadow: "none" }}
            />
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            <button type="button" className="aa-menu-item" data-active={active === 0} aria-selected={!f.ws} onClick={() => choose("")}>
              <span style={{ flex: 1 }}>All workspaces</span>
              {!f.ws && <Icon name="check" size={15} strokeWidth={2.5} style={{ color: "var(--aa-accent)" }} />}
            </button>
            {list.map((a, i) => (
              <button key={a.accountId} type="button" className="aa-menu-item" data-active={active === i + 1} aria-selected={f.ws === a.accountId} onClick={() => choose(a.accountId)}>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{workspaceLabel(a.accountId)}</span>
                <span className="aa-num" style={{ fontSize: 12, color: "var(--aa-muted)" }}>
                  {compact(a.runs)} req
                </span>
                {f.ws === a.accountId && <Icon name="check" size={15} strokeWidth={2.5} style={{ color: "var(--aa-accent)" }} />}
              </button>
            ))}
            {!list.length && <div style={{ padding: "12px 10px", fontSize: 13, color: "var(--aa-muted)" }}>No workspace matches.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
